import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import styles from './UserSelect.module.css';
import Modal from '../Modal/Modal';
import User from '../Forms/User/User';
import { IUser } from '../../models/IUser';

interface UserSelectProps {
	/** Список пользователей для выбора */
	options: IUser[];
	/** Callback при выборе — возвращает полный объект IUser
	 *  или {name, surname} при ручном вводе (allowCustomInput) */
	callback?: (value: any) => void;
	/** Текст-плейсхолдер (напр. "Dykkeleder") */
	context?: string;
	/** Предустановленное значение {id?, name, surname?} */
	values?: any;
	/** Показать кнопку "Добавить нового пользователя" */
	addnew?: boolean;
	/** Разрешить ручной ввод имени (замена addInput для SJABottom) */
	allowCustomInput?: boolean;
	/** Сбросить выбранное значение */
	clear?: boolean;
}

const UserSelect: FC<UserSelectProps> = ({
	options,
	callback,
	context,
	values,
	addnew,
	allowCustomInput,
	clear,
}) => {
	const [selectedUser, setSelectedUser] = useState<any>(null);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [modal, setModal] = useState<boolean>(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Инициализация предустановленного значения
	useEffect(() => {
		if (values) {
			const v = { ...values };
			if (!v.surname) v.surname = '';
			setSelectedUser(v);
			setSearchTerm('');
		}
	}, [values]);

	// Сброс при clear
	useEffect(() => {
		if (clear) {
			setSelectedUser(null);
			setSearchTerm('');
		}
	}, [clear]);

	// Закрытие дропдауна при клике вне компонента
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				// Если разрешён ручной ввод и есть текст — зафиксировать
				if (allowCustomInput && searchTerm && !selectedUser) {
					const customUser = { name: searchTerm, surname: '' };
					setSelectedUser(customUser);
					setSearchTerm('');
					callback && callback(customUser);
				}
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, [searchTerm, selectedUser, allowCustomInput, callback]);

	// Фильтрация по имени/фамилии
	// СТАЛО:
	const filteredOptions = options
		.filter((user) => {
			if (!searchTerm) return true;
			const fullName = `${user.name} ${user.surname}`.toLowerCase();
			return fullName.includes(searchTerm.toLowerCase());
		})
		.sort((a, b) => {
			const nameA = `${a.name} ${a.surname}`.toLowerCase();
			const nameB = `${b.name} ${b.surname}`.toLowerCase();
			return nameA.localeCompare(nameB, 'no');
		});

	const handleOpen = useCallback(() => {
		setIsOpen(true);
		setSearchTerm('');
		setTimeout(() => inputRef.current?.focus(), 50);
	}, []);

	const handleSelect = useCallback(
		(user: IUser) => {
			setSelectedUser(user);
			setSearchTerm('');
			setIsOpen(false);
			callback && callback(user);
		},
		[callback],
	);

	const handleCustomInput = useCallback(
		(text: string) => {
			const customUser = { name: text, surname: '' };
			setSelectedUser(customUser);
			setSearchTerm('');
			callback && callback(customUser);
		},
		[callback],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				if (filteredOptions.length === 1) {
					handleSelect(filteredOptions[0]);
				} else if (allowCustomInput && searchTerm) {
					handleCustomInput(searchTerm);
					setIsOpen(false);
				}
			}
			if (e.key === 'Escape') {
				setIsOpen(false);
			}
		},
		[
			filteredOptions,
			searchTerm,
			allowCustomInput,
			handleSelect,
			handleCustomInput,
		],
	);

	const displayValue = selectedUser
		? `${selectedUser.name} ${selectedUser.surname || ''}`.trim()
		: '';

	const placeholder = `Velg ${context || ''}`;

	return (
		<div className={styles.userSelect} ref={containerRef}>
			{/* Голова — показывает выбранного или плейсхолдер */}
			<div className={styles.userSelect__head} onClick={handleOpen}>
				<div className={styles.userSelect__value}>
					{displayValue || placeholder}
				</div>
			</div>

			{/* Дропдаун с поиском */}
			{isOpen && (
				<div className={styles.userSelect__body}>
					<div className={styles.userSelect__search}>
						<input
							ref={inputRef}
							type="text"
							className={styles.userSelect__searchInput}
							placeholder="Søk etter navn..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={handleKeyDown}
						/>
					</div>

					<div className={styles.userSelect__list}>
						{filteredOptions.length > 0 ? (
							filteredOptions.map((user) => (
								<div
									key={user.id || (user as any)._id}
									className={styles.userSelect__item}
									onClick={() => handleSelect(user)}
								>
									{user.name} {user.surname}
								</div>
							))
						) : (
							<div className={styles.userSelect__empty}>
								Ingen treff
								{allowCustomInput && searchTerm && (
									<span
										className={
											styles.userSelect__customAction
										}
										onClick={() => {
											handleCustomInput(searchTerm);
											setIsOpen(false);
										}}
									>
										{` — bruk "${searchTerm}"`}
									</span>
								)}
							</div>
						)}
					</div>

					{addnew && (
						<div
							className={styles.userSelect__addNew}
							onClick={() => {
								setModal(true);
								setIsOpen(false);
							}}
						>
							+ Leg til ny {context || ''}
						</div>
					)}
				</div>
			)}

			<Modal
				isVisible={modal}
				title={`Leg til ny ${context || ''}`}
				onClose={() => setModal(false)}
				content={
					<User
						type="new"
						callback={() => {
							setModal(false);
							callback && callback(selectedUser);
						}}
					/>
				}
				widthVh={'auto'}
				heightVh={'auto'}
			/>
		</div>
	);
};

export default observer(UserSelect);
